import { KeyboardEvent } from 'react';
import { NavLink } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import {
    Calendar,
    Bullhorn,
    UserMultiple,
    PedestrianFamily, 
    Settings,
    CalendarSettings,
    Book,
    Money,
    ShoppingCart,
    TaskTools,
} from '@carbon/icons-react';

import styles from './LeftMenuPanel.module.scss';
import LogoutHandler from './Logout/Logout';
import useGetCurrentEntity from 'hooks/useGetCurrentEntity';
import { ChurchLogo } from 'Components/Common/icons/ChurchLogo';
import sharedMessages from 'Components/utils/sharedMessages';
import useGetUser from 'hooks/useGetUser';
import {
    OPEN_INITIAL_SETUP_EVENT,
    OPEN_JOIN_EXISTING_PARISH_EVENT,
} from 'Screens/Dashboard/InitialSetup/constants';
import { OPEN_ADD_PARISH_EVENT } from 'Screens/Dashboard/AllEntities/constants';

const LeftMenuPanel = () => {
    const intl = useIntl();
    const currentEntity = useGetCurrentEntity();
    const user = useGetUser();
    const hasAnyEntity = Boolean(user?.entities.length);
    const requiresInitialSetup = Boolean(user && !user.hasInitialSetupCompleted);
    const requiresParish = Boolean(user?.hasInitialSetupCompleted && !hasAnyEntity);
    const topActionItems = [
        ...(user ? [{
            label: intl.formatMessage(messages.joinExistingParish),
            icon: PedestrianFamily,
            highlighted: true,
            onClick: () => {
                window.dispatchEvent(new Event(OPEN_JOIN_EXISTING_PARISH_EVENT));
            },
        }] : []),
        ...((requiresInitialSetup || requiresParish) ? [{
            label: intl.formatMessage(requiresParish ? messages.addParish : messages.initialSetup),
            icon: TaskTools,
            highlighted: true,
            onClick: () => {
                window.dispatchEvent(new Event(requiresParish ? OPEN_ADD_PARISH_EVENT : OPEN_INITIAL_SETUP_EVENT));
            },
        }] : []),
    ];
    const topActionSections = topActionItems.length > 0 ? [{
        title: '',
        items: topActionItems,
    }] : [];
    const menuSections = [
        ...topActionSections,
        {
            title: intl.formatMessage(messages.sectionMain),
            items: [
                { label: intl.formatMessage(messages.events), link: '/events', icon: Calendar },
                { label: intl.formatMessage(messages.announcements), link: '/announcements', icon: Bullhorn },
                { label: intl.formatMessage(sharedMessages.offers), link: '/offers', icon: ShoppingCart },
            ],
        },
        {
            title: intl.formatMessage(messages.sectionPeople),
            items: [
                { label: intl.formatMessage(messages.peopleInParish), link: '/team', icon: UserMultiple },
                { label: intl.formatMessage(messages.communities), link: '/entities', icon: PedestrianFamily },
                { label: intl.formatMessage(sharedMessages.parishTeam), link: '/parish-book', icon: Book },
            ],
        },
        {
            title: intl.formatMessage(messages.sectionSettings),
            items: [
                { label: intl.formatMessage(messages.parishSettings), link: '/settings', icon: Settings },
                { label: intl.formatMessage(messages.eventsConfig), link: '/events-configuration', icon: CalendarSettings },
                { label: intl.formatMessage(sharedMessages.subscription), link: '/subscription', icon: Money },
            ],
        },
    ];

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const items = Array.from(
                document.querySelectorAll<HTMLElement>('[role="menuitem"]'),
            );
            const index = items.indexOf(document.activeElement as HTMLElement);
            
            if (e.key === 'ArrowDown') {
                const nextIndex = (index + 1) % items.length;
                items[nextIndex]?.focus();
            } else {
                const prevIndex = index - 1 < 0 ? items.length - 1 : index - 1;
                items[prevIndex]?.focus();
            }
        }
    };

    return (
        <aside className={styles.sidebar}>
            <header className={styles.header}>
                <div className={styles.logoPlaceholder}>
                    <ChurchLogo />
                </div>
                <div className={styles.headerText}>
                    <span className={styles.title}>Appostoł</span>
                    <span className={styles.subtitle}>{currentEntity?.name}</span>
                </div>
            </header>

            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <nav 
                className={styles.scrollArea} 
                onKeyDown={handleKeyDown} 
                aria-label="Menu nawigacji"
            >
                {menuSections.map((section) => (
                    <div key={section.title} className={styles.section}>
                        {section.title && <div className={styles.sectionTitle}>{section.title}</div>}
                        {section.items.map((item) => {
                            if ('onClick' in item) {
                                return (
                                    <button
                                        key={item.label}
                                        type="button"
                                        role="menuitem"
                                        className={`${styles.menuItem} ${item.highlighted ? styles.highlightedMenuItem : ''}`}
                                        onClick={item.onClick}
                                    >
                                        <item.icon size={20} />
                                        <span>{item.label}</span>
                                    </button>
                                );
                            }

                            return (
                                <NavLink
                                    key={item.link}
                                    to={item.link}
                                    role="menuitem"
                                    className={({ isActive }) => 
                                        `${styles.menuItem} ${isActive ? styles.active : ''}`}
                                >
                                    <item.icon size={20} />
                                    <span>{item.label}</span>
                                </NavLink>
                            );
                        })}
                    </div>
                ))}
            </nav>

            <footer className={styles.footer}>
                <LogoutHandler />
            </footer>
        </aside>
    );
};

const messages = defineMessages({
    sectionMain: { id: 'menu.section.main', defaultMessage: 'Panel Główny' },
    sectionPeople: { id: 'menu.section.people', defaultMessage: 'Ludzie' },
    sectionSettings: { id: 'menu.section.settings', defaultMessage: 'Ustawienia' },
    sectionHelp: { id: 'menu.section.help', defaultMessage: 'Pomoc' },
    
    events: { id: 'menu.item.events', defaultMessage: 'Plan' },
    announcements: { id: 'menu.item.announcements', defaultMessage: 'Ogłoszenia' },
    intentions: { id: 'menu.item.intentions', defaultMessage: 'Intencje mszalne' },
    
    peopleInParish: { id: 'menu.item.peopleInParish', defaultMessage: 'Ludzie' },
    communities: { id: 'menu.item.communities', defaultMessage: 'Wspólnoty i grupy' },
    
    parishSettings: { id: 'menu.item.parishSettings', defaultMessage: 'Ustawienia' },
    eventsConfig: { id: 'menu.item.eventsConfig', defaultMessage: 'Konfiguracja wydarzeń' },
    
    help: { id: 'menu.item.help', defaultMessage: 'Pomoc' },
    initialSetup: {
        id: 'src.Components.LeftMenuPanel.LeftMenuPanel.initialSetup',
        defaultMessage: 'Doko\u0144cz konfiguracj\u0119',
    },
    addParish: {
        id: 'src.Components.LeftMenuPanel.LeftMenuPanel.addParish',
        defaultMessage: 'Dodaj parafię',
    },
    initialConfiguration: {
        id: 'src.Components.LeftMenuPanel.LeftMenuPanel.initialConfiguration',
        defaultMessage: 'Wstępna konfiguracja',
    },
    joinExistingParish: {
        id: 'src.Components.LeftMenuPanel.LeftMenuPanel.joinExistingParish',
        defaultMessage: 'Dołącz do istniejącej parafii',
    },
});

export default LeftMenuPanel;
